// Reprise de base (SA-8) — importer FIDÈLEMENT une base héritée vers un magasin déjà en service, SANS PERTE.
// Doctrine (portée telle quelle de la maquette) : on reprend TOUT tel quel (stock négatif, client sans e-mail,
// doublon de code-barres) ; on n'écarte QUE l'impossible techniquement, jamais par jugement de contenu. Deux
// exceptions imposées par la loi : la chaîne fiscale NF525 repart NEUVE (compteurs à 1, l'historique = données,
// pas pièces signées) ; les titres actifs gardent leur code-barres d'origine (scannables le lendemain). La
// reprise REMPLIT une base, elle ne la crée pas → le magasin doit déjà être en service.
// ⚠️ §23.0 : le moteur EST branché depuis la phase 3a — connexion réelle en lecture seule, inventaire des
// tables, import (dry-run par défaut) et rapport de fidélité viennent tous du serveur. Rien n'est simulé :
// ni progression, ni rapport. RESTE non branché : les SIGNALEMENTS post-import de la maquette (stock
// négatif, clients sans e-mail, doublons de code-barres) — le rapport ne les remonte pas encore, la carte
// affiche donc un état vide honnête plutôt qu'une liste inventée.
import { Fragment, useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { api, type StoreRow, type LegacyDatabase, type LegacyTable, type LegacyImportReport } from '../api';
import { alert, confirm, showToast } from '@orgazone/ui-web';
import { EmptyState } from '@orgazone/ui-web';
import { Pill as UiPill, Spin } from '../ui';

// Pilotes de base source pris en charge à terme (port par défaut affiché en placeholder).
const DRIVERS = [
  { k: 'mysql', label: 'MySQL / MariaDB', port: '3306' },
  { k: 'pg', label: 'PostgreSQL', port: '5432' },
  { k: 'mssql', label: 'SQL Server', port: '1433' },
] as const;
type Driver = (typeof DRIVERS)[number]['k'];

// Les deux seules entorses à « on reprend tout tel quel » — imposées par la réglementation, pas par nous.
const LAW = [
  "La chaîne fiscale repart neuve : les compteurs NF525 démarrent à 1 sur la base d'arrivée. L'historique est repris comme données, pas comme pièces signées.",
  "Les titres actifs conservent leur code-barres d'origine, pour rester scannables en caisse le lendemain de la bascule.",
];

const field: CSSProperties = { width: '100%', height: 34, padding: '0 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontFamily: 'ui-monospace, monospace', fontSize: 'var(--t-dense)', outline: 'none' };
const lbl: CSSProperties = { display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 4 };
const step: CSSProperties = { fontFamily: 'ui-monospace, monospace', fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)', margin: '16px 0 8px' };
const cardHead: CSSProperties = { padding: '13px 18px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' };

// Pastille sémantique — géométrie « tight » (cf. ../ui) ; signature locale conservée.
const Pill = ({ k, children }: { k: 'ok' | 'in' | 'ne'; children: ReactNode }) => <UiPill tone={k} shape="tight">{children}</UiPill>;

// Taille lisible d'une base (octets → Ko/Mo/Go).
const fmtSize = (b: number) => (b >= 1e9 ? `${(b / 1e9).toFixed(1)} Go` : b >= 1e6 ? `${Math.round(b / 1e6)} Mo` : `${Math.max(1, Math.round(b / 1e3))} Ko`);
const fmtN = (n: number) => n.toLocaleString('fr-FR');

// Serveurs source ENREGISTRÉS (localStorage) — évite de resaisir hôte/port/identifiant à chaque reprise.
// ⚠️ SÉCURITÉ (§SA-8.1) : le MOT DE PASSE n'est JAMAIS enregistré (compte lecture seule) → toujours ressaisi.
const SOURCES_KEY = 'oz.reprise.sources';
interface SavedSource { driver: Driver; host: string; port: string; user: string }
const srcKey = (s: SavedSource) => `${s.host}:${s.port}:${s.user}`;
function loadSources(): SavedSource[] {
  try { const v = JSON.parse(localStorage.getItem(SOURCES_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}
function saveSource(s: SavedSource): SavedSource[] {
  const next = [s, ...loadSources().filter((x) => srcKey(x) !== srcKey(s))].slice(0, 8); // 8 derniers
  try { localStorage.setItem(SOURCES_KEY, JSON.stringify(next)); } catch { /* localStorage indispo/quota — best-effort */ }
  return next;
}
function forgetSource(s: SavedSource): SavedSource[] {
  const next = loadSources().filter((x) => srcKey(x) !== srcKey(s));
  try { localStorage.setItem(SOURCES_KEY, JSON.stringify(next)); } catch { /* best-effort */ }
  return next;
}

/**
 * ATTEND QUE LE SERVEUR DISE COMMENT ÇA S'EST TERMINÉ, quand la requête d'origine a été coupée.
 *
 * ⚠️ ELLE EXISTE PARCE QUE LA COUPURE EST NORMALE, pas exceptionnelle : le balanceur rend un 504 au bout
 * de 300 s et une reprise réelle dure plus longtemps. Le travail continue côté serveur ; le suivi, lui,
 * garde son verdict une demi-heure. Il suffit donc de continuer à demander.
 *
 * ⚠️ ELLE ABANDONNE AU BOUT DE 20 MINUTES plutôt que de tourner sans fin : un écran qui interroge pour
 * toujours finit par mentir aussi, en laissant croire qu'il sait encore quelque chose. Rendre `null`
 * veut dire « je ne sais pas », et l'appelant le DIT.
 */
async function attendreLIssue<T extends { done: boolean; report: unknown }>(
  releve: () => Promise<T>,
  maxMs = 20 * 60_000,
): Promise<T | null> {
  const fin = Date.now() + maxMs;
  while (Date.now() < fin) {
    await new Promise((r) => setTimeout(r, 3000));
    try {
      const p = await releve();
      if (p.done) return p;
    } catch { /* le serveur redémarre ou le réseau hoquette : on redemandera */ }
  }
  return null;
}

export function Legacy() {
  /** Le préfixe des identifiants qui relient chaque champ à son libellé ou au texte qui le présente. */
  const uid = useId();
  const [driver, setDriver] = useState<Driver>('pg'); // source actuelle = Postgres (copie d'un HFSQL PCsoft)
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  // ⚠️ TRANSPORT DE LA LIAISON — l'écran ne l'offrait PAS, et le serveur n'active TLS que sur demande :
  // toute reprise se faisait donc en clair, identifiants d'administration et base entière d'un client sur
  // le réseau. Le choix est ici, avec ses conséquences écrites (audit 2026-08-09 §7).
  const [ssl, setSsl] = useState(false);
  const [sslInsecure, setSslInsecure] = useState(false);
  const transport = { ssl, sslInsecure: ssl && sslInsecure };
  const [storeId, setStoreId] = useState('');
  const [stores, setStores] = useState<StoreRow[]>([]);
  // Connexion source RÉELLE (phase 1) : état + bases listées + base choisie.
  const [conn, setConn] = useState<'idle' | 'testing' | 'ok' | 'error'>('idle');
  const [dbs, setDbs] = useState<LegacyDatabase[]>([]);
  const [selectedDb, setSelectedDb] = useState('');
  const [serverVersion, setServerVersion] = useState('');
  const [connErr, setConnErr] = useState<string | null>(null);
  // Analyse RÉELLE de la base choisie (phase 2) : inventaire des tables + lignes attendues.
  const [analyzing, setAnalyzing] = useState(false);
  const [tables, setTables] = useState<LegacyTable[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [analyzeErr, setAnalyzeErr] = useState<string | null>(null);
  // Import réel (phase 3a) : dry-run par défaut (sûr) + rapport de fidélité.
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState<LegacyImportReport | null>(null);
  const [dryRun, setDryRun] = useState(true);
  // ⚠️ QUI FAIT FOI QUAND LES DEUX SOURCES DIVERGENT. Les défauts reproduisent le comportement
  //    historique (l'inventaire pour le stock, le grand livre pour la fidélité) : ouvrir le choix ne
  //    change donc rien tant que personne n'a tranché. Il n'y a pas de bonne réponse universelle —
  //    mesuré sur trois bases réelles, l'une accorde ses deux sources à 100 %, les autres à 59 et 68 %.
  // ⚠️ AUCUNE VALEUR PRÉREMPLIE : un défaut « photo » a fait tourner DEUX reprises réelles avec la
  // mauvaise source (2026-08-23, GIRAULT) — l'opérateur décidait « grand livre » et lançait sans voir
  // que le sélecteur disait autre chose. Choisir doit être un GESTE, pas un état hérité.
  const [arbStock, setArbStock] = useState<'' | 'photo' | 'cumul'>('');
  const [arbFid, setArbFid] = useState<'' | 'photo' | 'cumul'>('');
  // Tables DÉJÀ terminées pendant une reprise en cours — publiées par le serveur, jamais estimées.
  const [enCours, setEnCours] = useState<LegacyImportReport['tables']>([]);
  const [savedSources, setSavedSources] = useState<SavedSource[]>([]);

  // Magasins de DESTINATION = magasins déjà EN SERVICE (la reprise remplit une base existante, §23.0 : réel).
  // ⚠️ L'échec est CONSERVÉ : sans lui, le sélecteur affichait « aucun magasin en service » et bloquait
  // la reprise sans jamais dire que c'est la requête qui a échoué (§23.0).
  const [storesErr, setStoresErr] = useState<string | null>(null);
  useEffect(() => {
    api.stores().then((r) => { setStores(r.stores.filter((s) => s.status === 'active')); setStoresErr(null); })
      .catch((e) => setStoresErr((e as Error).message));
  }, []);
  useEffect(() => { setSavedSources(loadSources()); }, []);

  const credsOk = !!host.trim() && !!user.trim() && !!pass.trim();
  const portPh = DRIVERS.find((d) => d.k === driver)?.port ?? '';
  // Toute modif des paramètres source invalide la connexion en cours (on ne garde jamais une liste/analyse périmée).
  const resetConn = () => { setConn('idle'); setDbs([]); setSelectedDb(''); setConnErr(null); setTables([]); setTotalRows(0); setAnalyzeErr(null); };
  /**
   * RAMÈNE LE RAPPORT SOUS LES YEUX de qui vient de le demander.
   *
   * ⚠️ POURQUOI C'EST NÉCESSAIRE : les commandes (connexion, choix de la base, lancement) vivent dans la
   * colonne de DROITE, le rapport dans celle de GAUCHE. Sur une base réelle il compte plus de 200 tables,
   * donc la page devient très haute — on clique en bas et le résultat s'écrit tout en haut, hors champ.
   * L'opérateur devait remonter à la main à chaque essai. Ce n'est pas un artifice de confort : un
   * résultat qu'on ne voit pas est un résultat qui n'a pas été rendu.
   */
  const rapportRef = useRef<HTMLDivElement>(null);
  const montrerRapport = () => {
    // ⚠️ `prefers-reduced-motion` respecté : un défilement animé non désiré donne le vertige.
    const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rapportRef.current?.scrollIntoView({ behavior: doux ? 'smooth' : 'auto', block: 'start' });
  };

  // Analyse RÉELLE de la base choisie : inventaire des tables + comptage des lignes (lues attendues du rapport).
  const analyze = async (database: string) => {
    setAnalyzing(true); setAnalyzeErr(null); setTables([]); setTotalRows(0);
    // Dès le lancement : l'attente (« Analyse en cours… ») se montre AUSSI, sinon on croit qu'il ne se
    // passe rien pendant le comptage — qui prend plusieurs secondes sur une grosse base.
    montrerRapport();
    try {
      const r = await api.legacyAnalyze({ driver, host: host.trim(), port: port.trim() || undefined, user: user.trim(), password: pass, database, ...transport });
      setTables(r.tables); setTotalRows(r.totalRows);
    } catch (e) { setAnalyzeErr((e as Error).message); }
    finally { setAnalyzing(false); }
  };
  const pickDb = (name: string) => { setSelectedDb(name); void analyze(name); };
  const arbitrageChoisi = !!arbStock && !!arbFid;
  const canRun = conn === 'ok' && !!selectedDb && !!storeId && arbitrageChoisi;
  // Lance l'import (dry-run = compte sans écrire ; écriture = confirmée). Rapport de fidélité RÉEL.
  const runImport = async () => {
    // ⚠️ La double bretelle du bouton : l'arbitrage DOIT être un geste (cf. l'état initial vide) —
    // et ce test rétrécit les types, le corps travaille avec un choix certain.
    if (!canRun || importing || !arbStock || !arbFid) return;
    if (!dryRun) {
      const ok = await confirm({ title: 'Écrire la reprise dans la base ?', message: "L'import va ÉCRIRE dans la base du magasin (articles, clients, avoirs). Lancez d'abord une SIMULATION (dry-run) et validez son rapport. Action difficilement réversible.", confirmLabel: 'Écrire la reprise' });
      if (!ok) return;
    }
    setImporting(true); setReport(null); setEnCours([]);
    montrerRapport(); // la reprise se suit en direct : on montre le panneau qui la raconte
    // ⚠️ On interroge le SUIVI RÉEL (tables achevées côté serveur) plutôt que d'animer une barre :
    // une reprise de plusieurs minutes doit montrer ce qu'elle a fait, pas ce qu'on suppose (§23.0).
    /**
     * ⚠️ L'INSTANT DE DÉMARRAGE DU SERVEUR, RETENU AU PREMIER RELEVÉ. C'est la seule chose qui
     * distingue « le serveur n'a rien à suivre » de « le serveur a REDÉMARRÉ pendant le travail » :
     * dans les deux cas la file de suivi est vide, parce qu'elle vit en mémoire du processus.
     * Sans ce repère, un plantage se lisait exactement comme un non-événement (constaté le
     * 2026-08-17 : « Réponse illisible du serveur », sans rien d'autre).
     */
    let bootVu: number | null = null;
    let redemarre = false;
    const releve = async () => {
      const p = await api.legacyImportProgress(storeId);
      if (bootVu === null) bootVu = p.bootedAt;
      else if (p.bootedAt !== bootVu) redemarre = true;
      setEnCours(p.tables);
      return p;
    };
    const suivi = window.setInterval(() => {
      void releve().catch(() => undefined); // une interrogation ratée n'interrompt rien : la suivante reprendra
    }, 1500);
    try {
      const r = await api.legacyImport({ driver, host: host.trim(), port: port.trim() || undefined, user: user.trim(), password: pass, database: selectedDb, storeId, dryRun, arbitrage: { stock: arbStock, fidelite: arbFid }, ...transport });
      setReport(r);
      if (!r.ok) void alert({ title: 'Reprise interrompue', message: r.error ?? 'erreur inconnue' });
    } catch (e) {
      /**
       * ⚠️ PERDRE LA RÉPONSE N'EST PAS ÉCHOUER — et c'est le cas le PLUS FRÉQUENT sur une vraie base.
       * Le balanceur coupe à 300 s ; une reprise sérieuse dure davantage, et le serveur, lui, va au
       * bout. L'écran annonçait alors « Échec de la reprise » sur un travail qui réussissait : le
       * 2026-08-17, MEN FACTORY a écrit 379 682 articles et ses 1 727 ventes abandonnées pendant que
       * l'opérateur lisait un échec. C'est exactement le mensonge que §23.0 interdit, à l'envers.
       * On ne conclut donc RIEN ici : on continue de relever le suivi jusqu'à ce que le serveur dise.
       */
      const issue = redemarre ? null : await attendreLIssue(releve);
      if (issue?.report) {
        setReport(issue.report);
        if (!issue.report.ok) void alert({ title: 'Reprise interrompue', message: issue.report.error ?? 'erreur inconnue' });
        else {
          showToast('Reprise terminée — la connexion avait été coupée, pas la reprise.', 'info', 8000);
        }
      } else if (redemarre) {
        void alert({
          title: 'Le serveur a redémarré pendant la reprise',
          message: 'C’est ce qui a coupé la réponse.\n\nCause la plus fréquente : épuisement mémoire sur une '
            + 'base volumineuse (la reprise lit les tables entières). Le suivi vit en mémoire du processus : '
            + 'il a disparu avec lui.\n\n'
            + (dryRun
              ? 'C’était une SIMULATION : rien n’a été écrit dans la base du magasin.'
              : 'Écriture réelle : ce qui avait été validé avant la coupure est écrit, le reste ne l’est pas. '
                + 'Relancez — la reprise reconnaît l’existant et ne complète que les manques.'),
          tone: 'err',
        });
      } else {
        // ⚠️ ON NE SAIT PAS, ET ON LE DIT. Ni « réussi » ni « échoué » : la seule chose établie est que
        // la Console a perdu le contact. Trancher à sa place serait inventer un fait (§23.0).
        void alert({
          title: 'Contact perdu avec le serveur',
          message: `La Console n’a pas pu lire l’issue de la reprise : ${(e as Error).message}\n\n`
            + 'Cela ne veut PAS dire qu’elle a échoué — le serveur travaille peut-être encore. Rouvrez cet '
            + 'écran dans quelques minutes : l’issue reste consultable une demi-heure. '
            + (dryRun ? '' : 'Une reprise relancée reconnaît l’existant et ne complète que les manques.'),
          tone: 'err',
        });
      }
    }
    finally { window.clearInterval(suivi); setImporting(false); }
  };
  // Connexion RÉELLE lecture seule → liste les bases du serveur. Postgres seul branché pour l'instant.
  const connect = async () => {
    if (driver !== 'pg') { showToast("Seul PostgreSQL est branché pour l'instant (la source actuelle est Postgres).", 'warn'); return; }
    setConnErr(null); setSelectedDb(''); setConn('testing');
    try {
      const r = await api.legacyDatabases({ driver, host: host.trim(), port: port.trim() || undefined, user: user.trim(), password: pass, ...transport });
      setServerVersion(r.serverVersion); setDbs(r.databases); setConn('ok');
      // Enregistre le serveur (SANS le mot de passe) pour ne pas resaisir hôte/port/identifiant la prochaine fois.
      setSavedSources(saveSource({ driver, host: host.trim(), port: port.trim(), user: user.trim() }));
    } catch (e) { setConn('error'); setConnErr((e as Error).message); }
  };
  const connPill = conn === 'ok' ? { k: 'ok' as const, t: 'connecté' } : conn === 'testing' ? { k: 'in' as const, t: 'connexion…' } : { k: 'ne' as const, t: 'hors ligne' };

  return (
    <div className="page">
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Reprise de base</h1>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3, maxWidth: 660, lineHeight: 1.5 }}>Import fidèle d'une base historique vers un magasin déjà en service. Ce qui existe est repris tel quel.</div>
        </div>
        <div style={{ flex: 1 }} />
        {/* Pastille DYNAMIQUE — un libellé figé mentirait dès le premier import (§23.0). */}
        <Pill k={report ? (report.dryRun ? 'in' : 'ok') : 'ne'}>{report ? (report.dryRun ? 'simulation effectuée' : 'reprise effectuée') : 'aucune reprise lancée'}</Pill>
      </div>

      {/* Bandeau doctrine — « on reprend tel quel » */}
      <div style={{ display: 'flex', gap: 11, padding: '14px 16px', borderRadius: 'var(--r-sm)', border: '1px solid rgba(56,189,248,.3)', background: 'var(--in-bg)', marginBottom: 14 }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--in-fg)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8v.5" /></svg>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--in-fg)', lineHeight: 1.6 }}>Un stock négatif, un client sans e-mail, un doublon de code-barres font partie de la vie du magasin : ils sont <b>repris tels quels</b>. Rien n'est corrigé, rien n'est arbitré, rien n'est refusé — sinon la base d'arrivée ne raconterait plus la même histoire que celle du client.</div>
      </div>


      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 330px', gap: 14, alignItems: 'start' }}>
        {/* ── Colonne principale : rapport de fidélité + signalements (vides tant qu'aucune reprise) ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          {/* ⚠️ `scroll-margin-top` : la barre supérieure est collante, sans marge elle recouvrirait
              l'en-tête du rapport qu'on vient justement d'amener à l'écran. */}
          <div ref={rapportRef} className="dcard" style={{ overflow: 'hidden', scrollMarginTop: 16 }}>
            <div style={cardHead}>
              <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Rapport de fidélité</span>
              <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>
                {report ? `${report.dryRun ? 'SIMULATION (dry-run)' : 'ÉCRITURE'} · ${selectedDb} → ${report.storeGln} · ${fmtN(report.tables.reduce((s, t) => s + t.written, 0))} ${report.dryRun ? 'à écrire' : 'écrites'}`
                  : tables.length ? `analyse de ${selectedDb} · ${tables.length} tables · ${fmtN(totalRows)} lignes` : 'aucune reprise lancée pour l\'instant'}
              </span>
            </div>
            {importing ? (
              <div style={{ padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: enCours.length ? 14 : 0 }}>
                  <Spin size={16} />
                  <span style={{ fontSize: 'var(--t-lead)' }}>
                    {dryRun ? 'Simulation' : 'Écriture'} de la reprise en cours…
                    {enCours.length ? ` — ${enCours.length} table(s) terminée(s)` : ' (lecture de la source)'}
                  </span>
                </div>
                {/* Chaque ligne = une table RÉELLEMENT achevée, avec ses comptes réels. */}
                {enCours.map((t, i) => (
                  <div key={`${t.name}-${i}`} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '5px 0', borderTop: i ? '1px solid var(--ink200)' : undefined, fontSize: 'var(--t-dense)' }}>
                    <span style={{ color: t.error ? 'var(--da-fg)' : 'var(--ok-fg)', width: 14 }}>{t.error ? '✕' : '✓'}</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>{t.name}</span>
                    <span className="mono" style={{ color: 'var(--ink500)' }}>
                      {fmtN(t.read)} lues · {fmtN(t.written)} écrites{t.skipped ? ` · ${fmtN(t.skipped)} écartées` : ''}
                    </span>
                  </div>
                ))}
                {enCours.some((t) => t.note) && (
                  <div style={{ marginTop: 8, fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.5 }}>
                    {enCours.filter((t) => t.note).map((t, i) => <div key={i}>{t.name} : {t.note}</div>)}
                  </div>
                )}
              </div>
            ) : report ? (
              <>
                <div style={{ padding: '9px 18px', borderBottom: '1px solid var(--ink200)', fontSize: 'var(--t-meta)', lineHeight: 1.5, background: report.dryRun ? 'var(--in-bg)' : 'var(--ok-bg)', color: report.dryRun ? 'var(--in-fg)' : 'var(--ok-fg)' }}>
                  {report.dryRun ? <><b>Simulation (dry-run)</b> — rien n'a été écrit : voici ce qui SERAIT repris.</> : <><b>Écriture effectuée</b> dans la base du magasin.</>}
                  {report.error && <span style={{ color: 'var(--da-fg)' }}> ⚠️ Interrompu : {report.error}</span>}
                </div>
                {report.tables.map((t) => (
                  <div key={t.name} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', padding: '11px 18px', borderTop: '1px solid var(--ink100)' }}>
                    <div style={{ minWidth: 150, flex: 1 }}>
                      <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{t.name}</div>
                      <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', overflowWrap: 'anywhere' }}>{t.source}{t.note ? ` · ${t.note}` : ''}{t.error ? ` · ⚠️ ${t.error}` : ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <div><span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink500)' }}>lues </span><span className="mono" style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>{fmtN(t.read)}</span></div>
                      <div><span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink500)' }}>{report.dryRun ? 'à écrire ' : 'écrites '}</span><span className="mono" style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: t.error ? 'var(--da-fg)' : 'var(--ok-fg)' }}>{fmtN(t.written)}</span></div>
                      <div><span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink500)' }}>écartées </span><span className="mono" style={{ fontSize: 'var(--t-body)', fontWeight: t.skipped ? 700 : 600, color: t.skipped ? 'var(--wa-fg)' : 'var(--ink400)' }}>{fmtN(t.skipped)}</span></div>
                    </div>
                    {/* ⚠️ LE DÉTAIL DES ÉCARTÉES SE DÉPLIE SOUS LA LIGNE, motif par motif. Le compteur seul
                        dit COMBIEN tombe, jamais QUOI : « 92 écartées » n'aide personne à décider si la
                        reprise est acceptable, « 92 mouvements sans prix dans la source » oui. */}
                    {t.dropped && t.dropped.length > 0 && (
                      <ul style={{ flexBasis: '100%', margin: 0, padding: '2px 0 0 0', listStyle: 'none', display: 'grid', gap: 3 }}>
                        {t.dropped.map((d, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 'var(--t-note)', lineHeight: 1.45 }}>
                            <span className="mono" style={{
                              fontSize: 'var(--t-note)', fontWeight: 700, minWidth: 58, textAlign: 'right',
                              color: d.lost ? 'var(--da-fg)' : 'var(--ink500)',
                            }}>{fmtN(d.count)}</span>
                            <span style={{ color: d.lost ? 'var(--da-fg)' : 'var(--ink500)' }}>
                              {d.reason}
                              {/* Ce qui est couvert ailleurs se DIT couvert : sans ça, tout se lit comme une perte. */}
                              {!d.lost && <span style={{ color: 'var(--ink400)' }}> — repris par ailleurs, rien de perdu</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* ⚠️ LE TABLEAU SUR LEQUEL ON TRANCHE. Le résumé dit COMBIEN divergent ; seul le
                        détail dit de QUOI il s'agit — et c'est ce qui permet de reconnaître, par
                        exemple, que les écarts se concentrent sur des articles jamais inventoriés.
                        Trié par impact : personne ne relira 8 805 lignes. */}
                    {t.reconciliation && t.reconciliation.divergences.length > 0 && (
                      <div style={{ flexBasis: '100%', paddingTop: 4 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '2px 14px', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
                          <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink400)' }}>clé</span>
                          <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink400)', textAlign: 'right' }}>grand livre</span>
                          <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink400)', textAlign: 'right' }}>photo</span>
                          <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink400)', textAlign: 'right' }}>écart</span>
                          {t.reconciliation.divergences.slice(0, 12).map((d) => (
                            <Fragment key={d.cle}>
                              <span className="mono" style={{ color: 'var(--ink600)', overflowWrap: 'anywhere' }}>{d.libelle ?? d.cle}</span>
                              <span className="mono" style={{ textAlign: 'right', color: 'var(--ink500)' }}>{fmtN(d.cumul)}</span>
                              <span className="mono" style={{ textAlign: 'right', color: 'var(--ink500)' }}>{fmtN(d.photo)}</span>
                              <span className="mono" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--wa-fg)' }}>{d.photo - d.cumul > 0 ? '+' : ''}{fmtN(d.photo - d.cumul)}</span>
                            </Fragment>
                          ))}
                        </div>
                        {/* ⚠️ CE QUI N'EST PAS MONTRÉ SE DIT — deux plafonds se cumulent (12 à l'écran,
                            200 transmis) et un tableau qui paraît complet ferait valider à l'aveugle. */}
                        {(t.reconciliation.divergences.length > 12 || t.reconciliation.divergencesNonListees > 0) && (
                          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink400)', marginTop: 4 }}>
                            … et {fmtN(t.reconciliation.total - t.reconciliation.accords - 12)} autre(s) désaccord(s) non affiché(s)
                            {t.reconciliation.divergencesNonListees > 0 && <> — dont {fmtN(t.reconciliation.divergencesNonListees)} non transmis à l’écran</>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {/* Le total de ce qui ne se retrouve NULLE PART dans la base cible, toutes tables confondues.
                    ⚠️ Il s'affiche même à zéro : « aucune donnée perdue » est une information, et c'est
                    justement celle que l'exploitant vient chercher avant de basculer son magasin. */}
                {(() => {
                  const perdu = report.tables.reduce(
                    (s, t) => s + (t.dropped ?? []).filter((d) => d.lost).reduce((a, d) => a + d.count, 0), 0);
                  // ⚠️ TABLEAU VIDE = étape qui a mesuré et n'a rien perdu ; ABSENT = étape qui ne mesure
                  // pas. Sans cette distinction, une reprise parfaite et une reprise non contrôlée
                  // afficheraient le même « aucune perte ».
                  const muettes = report.tables.filter((t) => !t.dropped && !t.error);
                  return (
                    <div style={{
                      padding: '10px 18px', borderTop: '1px solid var(--ink200)',
                      background: perdu > 0 ? 'var(--wa-bg)' : 'var(--ink100)',
                      color: perdu > 0 ? 'var(--wa-fg)' : 'var(--ink500)',
                      fontSize: 'var(--t-meta)', lineHeight: 1.5,
                    }}>
                      {perdu > 0
                        ? <><b>{fmtN(perdu)} lignes source ne se retrouvent nulle part</b> dans la base du magasin — motifs détaillés ci-dessus.</>
                        : <><b>Aucune ligne perdue</b> parmi les {report.tables.length - muettes.length} étapes qui chiffrent leur écart.</>}
                      {/* ⚠️ LA RÉSERVE EST NOMMÉE, PAS SOUS-ENTENDUE. Annoncer « aucune perte » alors que la
                          moitié des étapes ne regardent pas donnerait une garantie qu'on n'a pas — le pire
                          mensonge possible sur cet écran, parce qu'il rassure juste avant une bascule. */}
                      {muettes.length > 0 && (
                        <div style={{ marginTop: 4, color: 'var(--ink500)' }}>
                          {muettes.length} étape{muettes.length > 1 ? 's' : ''} ne chiffre{muettes.length > 1 ? 'nt' : ''} pas
                          encore son écart lecture→écriture — ce total ne dit rien de {muettes.length > 1 ? 'ces tables' : 'cette table'} :{' '}
                          <span style={{ color: 'var(--ink400)' }}>{muettes.map((t) => t.name).join(', ')}</span>.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            ) : analyzing ? (
              <div className="empty" style={{ padding: '26px 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}><Spin size={16} /><span>Analyse de <b>{selectedDb}</b> en cours… (comptage des lignes en lecture seule)</span></div>
            ) : analyzeErr ? (
              <div className="err" style={{ margin: 16 }}>{analyzeErr}</div>
            ) : tables.length ? (
              <>
                <div style={{ padding: '9px 18px', background: 'var(--in-bg)', borderBottom: '1px solid var(--ink200)', fontSize: 'var(--t-meta)', color: 'var(--in-fg)', lineHeight: 1.5 }}>
                  <b>Prévisionnel</b> — inventaire de la source (lignes à lire), en lecture seule. Les colonnes <i>écrites</i> · <i>écartées</i> se rempliront à la reprise ; rien n'est encore importé.
                </div>
                {tables.map((t) => (
                  <div key={`${t.schema}.${t.name}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', padding: '11px 18px', borderTop: '1px solid var(--ink100)' }}>
                    <div style={{ minWidth: 150, flex: 1 }}>
                      <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{t.name}</div>
                      <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', overflowWrap: 'anywhere' }}>{t.schema}.{t.name} · {t.columnCount} col.</div>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <div><span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink500)' }}>lignes </span><span className="mono" style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>{t.rowCount === null ? '—' : fmtN(t.rowCount)}</span></div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <EmptyState icon="file" title="Aucune analyse pour l’instant" note={<>Connectez-vous à un serveur source et <b>choisissez une base</b> : son inventaire (tables et lignes à lire) s’affichera ici. Rien n’est affiché tant qu’aucune analyse réelle n’a eu lieu.</>} />
            )}
            <div style={{ padding: '12px 18px', borderTop: '1px solid var(--ink200)', background: 'var(--ink100)', fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.45 }}>
              Une ligne n'est écartée que pour impossibilité technique — jamais parce que son contenu nous déplaît.
            </div>
          </div>

          <div className="dcard" style={{ overflow: 'hidden' }}>
            <div style={cardHead}>
              <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Signalements</span>
              <Pill k="ne">non bloquants</Pill>
              <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>pour information du magasin</span>
            </div>
            <EmptyState compact icon="bell" title="Aucun signalement" note={<>Les points relevés à l’import (stock négatif, clients sans e-mail, doublons de codes-barres…) — <b>non bloquants</b> — s’afficheront ici après une reprise.</>} />
          </div>
        </div>

        {/* ── Colonne latérale : lancer une reprise + exceptions légales ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="dcard" style={{ padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>Lancer une reprise</span>
              <div style={{ flex: 1, minWidth: 6 }} />
              <Pill k={connPill.k}>{conn === 'testing' ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Spin size={9} /> connexion…</span> : connPill.t}</Pill>
            </div>

            {/* 1 · serveur source (connexion RÉELLE lecture seule) */}
            <div style={step}>1 · serveur source</div>
            {savedSources.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <label htmlFor={`${uid}-saved`} style={lbl}>Serveur enregistré</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select id={`${uid}-saved`} value="" onChange={(e) => { const s = savedSources[Number(e.target.value)]; if (s) { setDriver(s.driver); setHost(s.host); setPort(s.port); setUser(s.user); setPass(''); resetConn(); } }}
                    style={{ flex: 1, minWidth: 0, height: 34, padding: '0 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)', outline: 'none', cursor: 'pointer' }}>
                    <option value="">— reprendre un serveur enregistré —</option>
                    {savedSources.map((s, i) => <option key={srcKey(s)} value={i}>{s.host}{s.port ? `:${s.port}` : ''} · {s.user} ({DRIVERS.find((d) => d.k === s.driver)?.label ?? s.driver})</option>)}
                  </select>
                  <button type="button" title="Oublier le serveur correspondant à l'hôte/identifiant saisis"
                    onClick={() => setSavedSources(forgetSource({ driver, host: host.trim(), port: port.trim(), user: user.trim() }))} className="btn-ghost" style={{ height: 34, padding: '0 11px', borderRadius: 'var(--r-sm)', fontSize: 'var(--t-note)', fontWeight: 600, flexShrink: 0 }}>Oublier</button>
                </div>
                <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 4 }}>Le mot de passe n'est jamais enregistré (compte lecture seule) — à ressaisir.</div>
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {DRIVERS.map((d) => {
                const on = driver === d.k;
                return (
                  <button key={d.k} onClick={() => { setDriver(d.k); resetConn(); }}
                    className={on ? 'field-opt selected' : 'field-opt'}>
                    {d.label}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 84px', gap: 8 }}>
              <div><label htmlFor={`${uid}-host`} style={lbl}>Hôte</label><input id={`${uid}-host`} value={host} onChange={(e) => { setHost(e.target.value); resetConn(); }} placeholder="192.168.1.20" style={field} /></div>
              <div><label htmlFor={`${uid}-port`} style={lbl}>Port</label><input id={`${uid}-port`} value={port} onChange={(e) => { setPort(e.target.value); resetConn(); }} placeholder={portPh} style={field} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8, marginTop: 8 }}>
              <div><label htmlFor={`${uid}-user`} style={lbl}>Identifiant</label><input id={`${uid}-user`} value={user} onChange={(e) => { setUser(e.target.value); resetConn(); }} placeholder="lecture_seule" style={field} /></div>
              <div><label htmlFor={`${uid}-pass`} style={lbl}>Mot de passe</label><input id={`${uid}-pass`} type="password" value={pass} onChange={(e) => { setPass(e.target.value); resetConn(); }} placeholder="••••••••" style={field} /></div>
            </div>
            {/* Chaque libellé dit ce qui se passe RÉELLEMENT sur le réseau — « SSL » tout seul laissait
                croire à une garantie d'authenticité que la connexion n'apportait pas. */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, fontSize: 'var(--t-dense)', color: 'var(--ink700)', cursor: 'pointer' }}>
              <input type="checkbox" checked={ssl} onChange={(e) => { setSsl(e.target.checked); resetConn(); }} style={{ marginTop: 2 }} />
              <span>Chiffrer la liaison (TLS)<br />
                <span style={{ color: 'var(--ink400)' }}>
                  {ssl ? 'Certificat du serveur vérifié.' : 'Décoché : identifiants et données transitent EN CLAIR.'}
                </span>
              </span>
            </label>
            {ssl && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 6, fontSize: 'var(--t-dense)', color: 'var(--ink700)', cursor: 'pointer' }}>
                <input type="checkbox" checked={sslInsecure} onChange={(e) => { setSslInsecure(e.target.checked); resetConn(); }} style={{ marginTop: 2 }} />
                <span>Accepter un certificat non vérifiable (serveur auto-signé)<br />
                  <span style={{ color: 'var(--ink400)' }}>
                    À n’utiliser qu’en réseau de confiance : la liaison reste chiffrée, mais rien ne prouve à qui l’on parle.
                  </span>
                </span>
              </label>
            )}
            <button disabled={!credsOk || conn === 'testing'} onClick={() => void connect()}
              style={{ width: '100%', height: 34, marginTop: 10, borderRadius: 'var(--r-sm)', border: '1px solid transparent', fontSize: 'var(--t-dense)', fontWeight: 700,
                background: credsOk && conn !== 'testing' ? 'var(--cta)' : 'var(--ink200)', color: credsOk && conn !== 'testing' ? 'var(--cta-ink)' : 'var(--ink400)', cursor: credsOk && conn !== 'testing' ? 'pointer' : 'not-allowed' }}>
              {conn === 'testing' ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Spin size={12} /> Connexion…</span> : conn === 'ok' ? 'Reconnecter' : 'Se connecter au serveur'}
            </button>
            <div style={{ fontSize: 'var(--t-meta)', marginTop: 8, lineHeight: 1.5, color: conn === 'error' ? 'var(--wa-fg)' : conn === 'ok' ? 'var(--ok-fg)' : 'var(--ink500)' }}>
              {conn === 'error' ? connErr
                : conn === 'ok' ? `Connecté (PostgreSQL ${serverVersion}) · lecture seule · ${dbs.length} base${dbs.length > 1 ? 's' : ''}.`
                : credsOk ? 'Connexion en LECTURE SEULE : la reprise ne doit jamais pouvoir écrire dans la base du client.'
                : 'Hôte, identifiant et mot de passe requis. Utilisez un compte en lecture seule.'}
            </div>

            {/* 2 · base à reprendre (liste RÉELLE après connexion) */}
            <div style={{ ...step, color: conn === 'ok' ? 'var(--ink500)' : 'var(--ink400)' }}>2 · base à reprendre</div>
            {conn === 'ok' ? (
              dbs.length === 0 ? <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink400)', lineHeight: 1.5 }}>Aucune base reprenable sur ce serveur.</div>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginBottom: 2 }}>Cliquez une base pour l'analyser (comptage des lignes en lecture seule → rapport de fidélité).</div>
                    {dbs.map((d) => {
                      const on = selectedDb === d.name;
                      return (
                        <button key={d.name} onClick={() => pickDb(d.name)}
                          className={on ? 'row-pick selected' : 'row-pick'}
                          style={{ display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', padding: '9px 11px', textAlign: 'left', color: 'var(--ink900)' }}>
                          <span style={{ width: 14, height: 14, borderRadius: '50%', flexShrink: 0, marginTop: 2, border: on ? '5px solid var(--teal)' : '1.5px solid var(--ink300)' }} />
                          <span style={{ minWidth: 0, flex: 1 }}>
                            <span className="mono" style={{ display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600 }}>{d.name}</span>
                            <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 2 }}>{fmtSize(d.sizeBytes)}{d.tableCount !== null ? ` · ${d.tableCount} tables` : ' · tables inaccessibles avec ce compte'}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
            ) : <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink400)', lineHeight: 1.5 }}>Les bases du serveur s'affichent après connexion — nous ne les devinons pas.</div>}

            {/* 3 · magasin de destination (RÉEL : magasins en service) */}
            <div id={`${uid}-0`} style={step}>3 · magasin de destination</div>
            <select aria-labelledby={`${uid}-0`} value={storeId} onChange={(e) => setStoreId(e.target.value)}
              style={{ width: '100%', height: 36, padding: '0 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: storeId ? 'var(--ink900)' : 'var(--ink400)', fontSize: 'var(--t-body)', outline: 'none', cursor: 'pointer' }}>
              <option value="">{storesErr ? '— liste indisponible —' : stores.length ? '— choisir un magasin —' : '— aucun magasin en service —'}</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.tenantName ?? '—'} · {s.name}{s.sCode ? ` · N°${s.sCode}` : ''}</option>)}
            </select>
            {storesErr
              ? <div className="err" style={{ marginTop: 6 }}>Magasins non chargés : {storesErr}</div>
              : <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 6, lineHeight: 1.5 }}>Le magasin doit déjà être en service : la reprise remplit une base, elle ne la crée pas.</div>}

            {/* ⚠️ L'ARBITRAGE SE POSE AVANT DE LANCER, et se lit dans le compte rendu. La base raconte
                souvent la même chose deux fois, et quand les deux récits divergent la machine ne peut
                pas savoir lequel a raison : cela dépend du magasin et de l'histoire de sa base. Faire
                une SIMULATION d'abord montre l'ampleur du désaccord avant de choisir. */}
            <div style={{ ...step, marginTop: 16 }}>4 · qui fait foi en cas de désaccord</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {([
                ['Stock', arbStock, setArbStock, 'l’inventaire (Stock_Article)', 'les mouvements (entrées − sorties)'],
                ['Fidélité', arbFid, setArbFid, 'l’historique de carte', 'le grand livre des points'],
              ] as const).map(([libelle, valeur, poser, photo, cumul]) => (
                <label key={libelle} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-dense)', color: 'var(--ink600)' }}>
                  <span style={{ minWidth: 62 }}>{libelle}</span>
                  <select value={valeur} onChange={(e) => poser(e.target.value as 'photo' | 'cumul')}
                    style={{ flex: 1, height: 32, padding: '0 8px', borderRadius: 'var(--r-sm)', border: `1px solid ${valeur ? 'var(--ink200)' : 'var(--wa-fg)'}`, background: 'var(--canvas)', color: valeur ? 'var(--ink900)' : 'var(--ink500)', fontSize: 'var(--t-dense)', outline: 'none', cursor: 'pointer' }}>
                    <option value="" disabled>— à choisir —</option>
                    <option value="photo">{photo}</option>
                    <option value="cumul">{cumul}</option>
                  </select>
                </label>
              ))}
            </div>
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 6, lineHeight: 1.5 }}>
              N'intervient que sur les entités où les deux sources <b>divergent</b> — le reste est repris tel quel. Le compte rendu chiffre le désaccord, et une simulation se relance à volonté pour comparer les deux choix.{!arbitrageChoisi && <> <b style={{ color: 'var(--wa-fg)' }}>Choisir les deux est requis pour lancer</b> — même une simulation : le compte rendu affiche les valeurs du choix, il ne doit jamais refléter un défaut que personne n'a posé.</>}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 14, fontSize: 'var(--t-dense)', color: 'var(--ink600)', cursor: 'pointer' }}>
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              <span>Simulation (dry-run) — lit, mappe et compte, <b>n'écrit rien</b></span>
            </label>
            <button disabled={!canRun || importing} onClick={() => void runImport()}
              style={{ width: '100%', height: 38, marginTop: 10, borderRadius: 'var(--r-sm)', border: '1px solid transparent', fontSize: 'var(--t-body)', fontWeight: 700,
                background: canRun && !importing ? (dryRun ? 'var(--cta)' : 'var(--da-fg)') : 'var(--ink200)', color: canRun && !importing ? (dryRun ? 'var(--cta-ink)' : '#fff') : 'var(--ink400)', cursor: canRun && !importing ? 'pointer' : 'not-allowed' }}>
              {importing ? (dryRun ? 'Simulation…' : 'Écriture…') : dryRun ? 'Simuler la reprise (dry-run)' : 'Lancer la reprise (écriture)'}
            </button>
            <div className="avenir" style={{ marginTop: 10 }}>
              <b>Phase 3a</b> : reprise du cluster fondational (<b>articles, clients + wallet, avoirs</b>) vers la base du magasin. Ventes/paiements atomisés (phase 3b) et fidélité/services (3c) à suivre. Faites toujours une <b>simulation</b> d'abord.
            </div>
          </div>

          {/* Deux exceptions légales */}
          <div className="dcard" style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginBottom: 9 }}>Deux exceptions, imposées par la loi</div>
            {LAW.map((l) => (
              <div key={l} style={{ display: 'flex', gap: 9, padding: '7px 0' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--teal)', marginTop: 7, flexShrink: 0 }} />
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.55 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
